import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { toast } from "react-toastify";
import { supabase } from "../../lib/supabase";
import Avatar from "../common/Avatar";

const PostComposer = ({ campaignId, onPostCreated, currentUser }) => {
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [postText, setPostText] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Logged-in user display name (from Supabase)
  const meta = currentUser?.user_metadata || {};
  const first = meta.first_name || meta.given_name || "";
  const last = meta.last_name || meta.family_name || "";
  const composed = [first, last].filter(Boolean).join(" ");
  const supaName =
    composed || meta.name || meta.full_name || currentUser?.email || "User";
  const supaAvatar = meta.avatar_url || meta.picture || "";

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    const validFiles = files.filter((file) => validTypes.includes(file.type));

    if (validFiles.length !== files.length) {
      toast.error("Only JPEG, PNG, and GIF images are allowed");
      return;
    }

    // Limit to 5 images
    if (selectedImages.length + validFiles.length > 5) {
      toast.error("Maximum 5 images allowed per post");
      return;
    }

    // Create preview URLs
    const newImages = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substr(2, 9),
    }));

    setSelectedImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (imageId) => {
    setSelectedImages((prev) => {
      const updated = prev.filter((img) => img.id !== imageId);
      // Clean up object URLs
      const toRemove = prev.find((img) => img.id === imageId);
      if (toRemove) {
        URL.revokeObjectURL(toRemove.preview);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!postText.trim() && selectedImages.length === 0) {
      toast.error("Please add some content or images to your post");
      return;
    }

    if (!currentUser) {
      toast.error("Please sign in to create a post");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("discription", postText);
      formData.append("name", "Campaign Post");
      formData.append("type", "campaign");
      formData.append("linkId", campaignId);

      // Add images to form data (backend expects field name 'assetts')
      selectedImages.forEach((image) => {
        formData.append("assetts", image.file);
      });

      // Get token from Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token =
        session?.access_token || localStorage.getItem("access_token");

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/gallery/post`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success("Post created successfully!");

        // Reset form
        setPostText("");
        setSelectedImages([]);
        setIsComposerOpen(false);

        // Clean up object URLs
        selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));

        // Notify parent component
        if (onPostCreated) {
          onPostCreated();
        }
      } else {
        throw new Error(result.message || "Failed to create post");
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error(error.message || "Failed to create post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetComposer = () => {
    setPostText("");
    setSelectedImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview));
      return [];
    });
    setIsComposerOpen(false);
  };

  // Auto-focus textarea when composer opens
  useEffect(() => {
    if (isComposerOpen && textareaRef.current) {
      textareaRef.current.focus();
      // Move caret to end
      const len = textareaRef.current.value?.length || 0;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isComposerOpen]);

  if (!currentUser) {
    return (
      <div className="mb-6 p-4 bg-jacarta-50 dark:bg-jacarta-800 rounded-2lg border border-jacarta-100 dark:border-jacarta-600">
        <p className="text-center text-jacarta-500 dark:text-jacarta-400">
          Please sign in to share your thoughts and photos about this campaign.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {!isComposerOpen ? (
        <div
          onClick={() => setIsComposerOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsComposerOpen(true);
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Compose a post"
          className="p-4 bg-white dark:bg-jacarta-800 rounded-2lg border border-jacarta-100 dark:border-jacarta-600 cursor-pointer hover:shadow-lg transition-all hover:border-accent/20 focus:outline-none focus-visible:ring-1 focus-visible:ring-jacarta-200 dark:focus-visible:ring-jacarta-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-jacarta-800"
        >
          <div className="flex items-center space-x-3">
            <Avatar creator={null} size="lg" nameFallback={supaName} />
            <div className="flex-1 px-4 py-2 bg-jacarta-50 dark:bg-jacarta-700 rounded-full">
              <p className="text-jacarta-500 dark:text-jacarta-400">
                Share your thoughts about this campaign...
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-white dark:bg-jacarta-800 rounded-2lg border border-jacarta-100 dark:border-jacarta-600 shadow-lg focus-within:outline-none focus-within:ring-2 focus-within:ring-accent/40 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-jacarta-800">
          <form onSubmit={handleSubmit}>
            <div className="flex items-start space-x-3 mb-4">
              <Avatar creator={null} size="lg" nameFallback={supaName} />
              <div className="flex-1">
                <textarea
                  ref={textareaRef}
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  placeholder="Share your thoughts about this campaign..."
                  className="w-full p-3 border-none bg-transparent resize-none text-jacarta-700 dark:text-white placeholder-jacarta-400 focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-transparent"
                  rows={3}
                  maxLength={2000}
                />
              </div>
            </div>

            {/* Image Preview Grid */}
            {selectedImages.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {selectedImages.map((image) => (
                    <div key={image.id} className="relative group">
                      <Image
                        src={image.preview}
                        alt="Preview"
                        width={200}
                        height={200}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-jacarta-100 dark:border-jacarta-600">
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center space-x-2 text-jacarta-500 hover:text-accent transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-sm">Photo</span>
                </button>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={resetComposer}
                  className="px-4 py-2 text-jacarta-500 hover:text-jacarta-700 dark:hover:text-white transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    (!postText.trim() && selectedImages.length === 0)
                  }
                  className="px-6 py-2 bg-accent text-white rounded-2lg hover:bg-accent-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-display font-semibold"
                >
                  {isSubmitting ? "Posting..." : "Post"}
                </button>
              </div>
            </div>
          </form>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

export default PostComposer;
